import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMemo } from 'react';
import {
  CropEmbeddingSearchResponse,
  EmbeddingSelectionMode,
} from '../../../types';

interface EmbeddingTileProps {
  embedding: CropEmbeddingSearchResponse;
  isSelected: boolean;
  canReject?: boolean;
  isDisabled: boolean;
  selectingMode: EmbeddingSelectionMode | null;
  onCheckboxChange: (embedding: CropEmbeddingSearchResponse) => void;
  onReject?: (embeddingId: string) => void;
}

const typeColors = {
  product: {
    border: '#D4D4D4',
    bg: 'bg-purple-100',
    text: 'text-purple-800',
  },
  carton: {
    border: '#D4D4D4',
    bg: 'bg-orange-100',
    text: 'text-orange-800',
  },
  template_box: {
    border: '#D4D4D4',
    bg: 'bg-indigo-100',
    text: 'text-indigo-800',
  },
};

export const EmbeddingTile: React.FC<EmbeddingTileProps> = ({
  embedding,
  isSelected,
  canReject,
  isDisabled,
  selectingMode,
  onCheckboxChange,
  onReject,
}) => {
  const typeColor = typeColors[embedding.classificationType];
  const badgeText = useMemo(() => {
    if (embedding.isPending) {
      return 'Pending';
    }
    return embedding.isActive ? 'Active' : 'Inactive';
  }, [embedding]);

  const tooltipText = useMemo(() => {
    if (isDisabled) {
      return selectingMode === EmbeddingSelectionMode.DEACTIVATING
        ? "It's already inactive"
        : "It's already active";
    }

    if (selectingMode) {
      return selectingMode === EmbeddingSelectionMode.DEACTIVATING
        ? 'Click to deactivate'
        : 'Click to activate';
    }

    return embedding.isActive ? 'Click to deactivate' : 'Click to activate';
  }, [isDisabled, embedding, selectingMode]);

  return (
    <div key={embedding.productEmbeddingCropId} className="relative group">
      {/* Trash icon for rejection */}
      {onReject && canReject && !isSelected && selectingMode === null && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onReject(embedding.productEmbeddingCropId || '');
          }}
          onMouseEnter={(e) =>
            e.currentTarget.closest('.group')?.classList.add('hover-disabled')
          }
          onMouseLeave={(e) =>
            e.currentTarget
              .closest('.group')
              ?.classList.remove('hover-disabled')
          }
          disabled={!canReject}
          className={
            'absolute top-2 right-2 z-10 p-2 rounded-full transition-colors bg-white bg-opacity-75 shadow text-gray-600 hover:bg-red-100 hover:text-red-600'
          }
          title={'Click to deactivate'}
        >
          <DeleteOutlineIcon />
        </button>
      )}

      <div
        onClick={() => !isDisabled && onCheckboxChange(embedding)}
        className={`flex items-center justify-center relative rounded-lg overflow-hidden ${
          isSelected ? 'border-4' : 'border'
        } ${!isDisabled ? 'cursor-pointer' : 'cursor-default'} ${
          !embedding.isActive ? 'p-2' : ''
        }`}
        style={{
          backgroundColor:
            !embedding.isActive && !embedding.isPending ? '#FFF3F0' : undefined,
          height: 'calc(100% - 35px)',
          borderColor: isSelected
            ? selectingMode === EmbeddingSelectionMode.DEACTIVATING
              ? '#ef4444'
              : '#22c55e'
            : !embedding.isActive && !embedding.isPending
            ? '#FB5149'
            : typeColor.border,
        }}
      >
        <img
          src={embedding.vizUrl}
          alt={`Embedding ${embedding.productEmbeddingCropId}`}
          className="w-auto h-auto"
        />

        {!isSelected && tooltipText && (
          <div
            className="absolute flex opacity-0 group-hover:opacity-100 group-[.hover-disabled]:opacity-0 transition-opacity bg-black bg-opacity-75 px-2 py-1 rounded-md pointer-events-none"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <span className="text-white text-sm font-medium whitespace-nowrap">
              {tooltipText}
            </span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-center gap-4 mt-2 text-sm">
        <div className="flex">
          <span
            className={`px-2 py-1 text-xs font-medium rounded ${
              embedding.isActive
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {badgeText}
          </span>
        </div>
        <div className="flex">
          <span
            className="px-2 py-1 text-xs font-medium rounded capitalize"
            style={{
              backgroundColor: '#CEC9E8',
              color: '#27272E',
            }}
          >
            {embedding.classificationType}
          </span>
        </div>
      </div>
    </div>
  );
};
